import { NumberHelper } from '@utils/numberHelper'
import { StoreExt } from '@utils/reactExt'
import { action, computed, observable, runInAction } from 'mobx'
import * as _ from 'lodash'
import CompanyStore from '@store/companyStore'
import MetaDataStore from '@store/metaDataStore'
import {
    ILayout,
    IWidgets,
    IItemsInWidget,
    IMenuWidget,
    ELayoutType,
    IMenuItem,
    EItemsInWidgetType
} from '@store/layoutStore/type.d'

export class LayoutStore extends StoreExt {
    @observable
    companyId = 0
    @observable
    activeLayoutType = ELayoutType.webNotConnected

    @observable
    widgetsMenu: IMenuWidget[] = []
    @observable
    itemsMenu: IMenuItem[] = []
    @observable
    modulesMenu: IMenuItem[] = CompanyStore.companyDetails.companiesModules.map(cm => {
        return {
            key: NumberHelper.getRnd(),
            moduleId: cm.moduleTypeId,
            label: MetaDataStore.moduleTypes.find(mt => mt.id === cm.moduleTypeId).name,
            type: EItemsInWidgetType.module
        }
    })
    @observable
    allItemsMenu = [...this.modulesMenu]
    @action
    getWidgetsMenu() {
        const moduleIds = CompanyStore.companyDetails.companiesModules.map(m => m.moduleTypeId)
        const widgets = MetaDataStore.widgetTypes.filter(w => moduleIds.includes(w.moduleId) || !w.moduleId)
        this.widgetsMenu = widgets.map(w => {
            return {
                key: w.id.toString(),
                type: w.id,
                label: w.name
            }
        })
    }

    @action
    async getLayouts(companyId) {
        const data: any[] = await this.api.layout.getLayouts(companyId)

        runInAction(() => {
            if (data.length > 0) {
                this.layouts = data
                this.layouts.forEach(l => {
                    this.alterDbLayout(l)
                })
            } else {
                this.layouts = this.newLayout
            }
            this.activeLayoutSource = JSON.stringify(this.layouts.slice().find(l => l.type === this.activeLayoutType))
            this.activeLayout = JSON.parse(this.activeLayoutSource)
        })
    }
    alterDbLayout(dbLayout: ILayout) {
        dbLayout.widgets.forEach(w => {
            w.key = NumberHelper.getRnd()
            w.label = MetaDataStore.widgetTypes.find(wt => wt.id === w.type).name
            if (w.itemsInWidget) {
                w.itemsInWidget = w.itemsInWidget.sort((a, b) => a.order - b.order)
                w.itemsInWidget.forEach(i => {
                    i.key = NumberHelper.getRnd()
                    switch (i.type) {
                        case EItemsInWidgetType.form:
                            i.label = this.itemsMenu.find(im => im.formGuid === i.formGuid)?.label
                            break
                        case EItemsInWidgetType.externalLink:
                            i.label = 'קישור חיצוני'
                            break
                        case EItemsInWidgetType.module:
                            i.label = this.modulesMenu.find(mm => mm.moduleId === i.modulesTypeId)?.label
                            break
                        default:
                            break
                    }
                })
            }
        })
        dbLayout.horizontallWidgets =
            dbLayout.widgets
                ?.filter(i => i.isHorizontal)
                .slice()
                .sort((a, b) => a.order - b.order) || []
        dbLayout.verticalWidget =
            dbLayout.widgets
                ?.filter(i => !i.isHorizontal)
                .slice()
                .sort((a, b) => a.order - b.order) || []
        return dbLayout
    }

    @action
    async getItemsMenu(companyId) {
        const data: any[] = await this.api.layout.getItemsMenu(companyId)
        runInAction(() => {
            this.itemsMenu = [
                { key: 'IM1', label: 'קישור חיצוני', type: EItemsInWidgetType.externalLink },
                ...data.map(i => {
                    return {
                        key: NumberHelper.getRnd(),
                        formGroupId: i.formGroupId,
                        formGuid: i.formGuid,
                        label: i.formTitle,
                        type: EItemsInWidgetType.form
                    }
                })
            ]
            this.allItemsMenu = [...this.allItemsMenu, ...this.itemsMenu]
        })
    }

    @observable
    layouts: ILayout[] = this.newLayout

    get newLayout(): ILayout[] {
        return [
            {
                verticalWidget: [],
                companyId: this.companyId,
                horizontallWidgets: [],
                type: 1
            }
        ]
    }

    @observable
    activeLayoutSource: string = JSON.stringify(this.layouts.slice().find(l => l.type === this.activeLayoutType))

    @observable
    activeLayout: ILayout = JSON.parse(this.activeLayoutSource)

    get isDirty() {
        return JSON.stringify(this.activeLayout) !== this.activeLayoutSource
    }
    @action
    reset() {
        this.activeLayout = JSON.parse(this.activeLayoutSource)
    }
    @action
    submit() {
        this.activeLayout.horizontallWidgets.forEach((item, index) => {
            item.order = index
            if (item.itemsInWidget) {
                item.itemsInWidget.forEach((iiw, index) => {
                    iiw.order = index
                })
            }
        })
        this.activeLayout.verticalWidget.forEach((item, index) => {
            item.order = index
            if (item.itemsInWidget) {
                item.itemsInWidget.forEach((iiw, index) => {
                    iiw.order = index
                })
            }
        })
        this.activeLayout.widgets = this.widgets
        this.api.layout.createOrUpdateLayout(this.activeLayout).then((res: ILayout) => {
            runInAction(() => {
                this.activeLayout = this.alterDbLayout(res)
                this.layouts = [...this.layouts.map(l => (l.type === this.activeLayoutType ? this.activeLayout : l))]
                this.activeLayoutSource = JSON.stringify(this.activeLayout)
            })
        })
    }

    @computed
    get horizontallWidgets() {
        return this.activeLayout.horizontallWidgets.slice()
    }

    @computed
    get verticalWidget() {
        return this.activeLayout.verticalWidget.slice()
    }

    @computed
    get widgets() {
        return [...this.activeLayout.horizontallWidgets, ...this.activeLayout.verticalWidget]
    }

    @action
    changeActiveLayout(value: number) {
        this.activeLayoutType = value
        const layout = this.layouts.slice().find(l => l.type === this.activeLayoutType)
        let newLayout: ILayout = null
        if (!layout) {
            newLayout = {
                verticalWidget: [],
                companyId: this.companyId,
                horizontallWidgets: [],
                type: value
            }
            this.layouts = [...this.layouts, newLayout]
        }

        this.activeLayoutSource = JSON.stringify(layout ? layout : newLayout)
        this.activeLayout = JSON.parse(this.activeLayoutSource)
    }
    @action
    setCompanyId(id: number) {
        this.companyId = id
        this.layouts[0].companyId = this.companyId
        this.activeLayout.companyId = this.companyId
        this.activeLayoutSource = JSON.stringify(this.activeLayout)
    }

    @action
    addWidget(widget: IWidgets, index: number) {
        widget.key = NumberHelper.getRnd()

        widget.isHorizontal
            ? this.activeLayout.horizontallWidgets.splice(index, 0, widget)
            : this.activeLayout.verticalWidget.splice(index, 0, widget)
    }
    @action
    addItmeToWidget(widgetId: string, item: IItemsInWidget, index: number) {
        const parent = this.widgets.find(i => i.key === widgetId)
        item.key = NumberHelper.getRnd()
        parent.itemsInWidget.splice(index, 0, item)
    }

    @action
    move(source, destination, droppableSource, droppableDestination, type) {
        const removed: IItemsInWidget | IWidgets = source.splice(droppableSource.index, 1)[0]
        removed.order = droppableDestination.index
        if (type === 'widgets') {
            // toggle position
            ;(removed as IWidgets).isHorizontal = !(removed as IWidgets).isHorizontal
        } else {
            ;(removed as IItemsInWidget).widgetId = this.widgets.find(
                i => i.key === droppableDestination.droppableId
            ).id
        }

        destination.splice(droppableDestination.index, 0, removed)
    }
    @action
    reorder(list, startIndex, endIndex) {
        const [removed] = list.splice(startIndex, 1)
        list.splice(endIndex, 0, removed)
        list.forEach((element, index) => (element.order = index))
    }
    @action
    removeItem(widget: IWidgets, item: IItemsInWidget) {
        if (widget.isHorizontal) {
            this.activeLayout.horizontallWidgets.find(i => i.key === widget.key).itemsInWidget = [
                ...this.activeLayout.horizontallWidgets
                    .find(i => i.key === widget.key)
                    .itemsInWidget.filter(i => i.key !== item.key)
            ]
            this.activeLayout.horizontallWidgets = [...this.activeLayout.horizontallWidgets]
        } else {
            this.activeLayout.verticalWidget.find(i => i.key === widget.key).itemsInWidget = [
                ...this.activeLayout.verticalWidget
                    .find(i => i.key === widget.key)
                    .itemsInWidget.filter(i => i.key !== item.key)
            ]
            this.activeLayout.verticalWidget = [...this.activeLayout.verticalWidget]
        }
    }
    @action
    removeWidget(widget: IWidgets) {
        if (widget.isHorizontal)
            this.activeLayout.horizontallWidgets = [
                ...this.activeLayout.horizontallWidgets.filter(i => i.key !== widget.key)
            ]
        else this.activeLayout.verticalWidget = [...this.activeLayout.verticalWidget.filter(i => i.key !== widget.key)]
    }
    @action
    duplicateToAllLayout() {
        const promises = []
        const appLayoutWidgets = this.transferVerticalToHorizntalWidget(this.activeLayout.widgets)
        for (let index = 1; index <= 4; index++) {
            promises.push(
                this.api.layout.createOrUpdateLayout({
                    ...this.activeLayout,
                    widgets:
                        index == 3 || index == 4
                            ? appLayoutWidgets
                            : [
                                  ...this.activeLayout.horizontallWidgets, // override only horizontall widgets
                                  ...this.layouts.find(l => l.type === index).verticalWidget
                              ],
                    type: index
                })
            )
        }
        Promise.all([...promises]).then(() => {
            this.getLayouts(this.companyId)
        })
    }
    @action
    transferVerticalToHorizntalWidget(widgets: IWidgets[]) {
        const vw = widgets.filter(w => w.isHorizontal)
        const maxOrder = Math.max(...vw.map(w => w.order))
        widgets.forEach((w, index) => {
            if (!w.isHorizontal) {
                w.isHorizontal = true
                w.order = maxOrder + index + 1
            }
        })
        return widgets
    }
}
export default new LayoutStore()
